import { Loader, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { PriceOfferGetResponse } from 'api/actions/price-offer-get/price-offer-get-response';
import { useApi } from 'api/api-context';
import PriceOfferProvideForm, { PriceOfferProvideFormData } from 'components/forms/price-offer/PriceOfferProvideForm';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PRICE_OFFER_PAGE_PATH, PRICE_OFFERS_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

/**
 * Page used by the assigned provider to submit or update a price offer.
 */
export default function ProvidePriceOfferPage() {
  const { getAction } = useApi();
  const { priceOfferId } = useParams();
  const navigate = useNavigate();
  const [priceOffer, setPriceOffer] = useState<PriceOfferGetResponse | null>(null);

  useEffect(() => {
    if (!priceOfferId) {
      return;
    }

    const action = getAction('PriceOfferGet');
    action({ parameters: { priceOfferId } }).then(setPriceOffer).catch(panic);
  }, [getAction, priceOfferId]);

  useEffect(() => {
    if (!priceOfferId || !priceOffer || priceOffer.actionPermissions.canProvide) {
      return;
    }

    navigate(PRICE_OFFER_PAGE_PATH.insert({ priceOfferId }), { replace: true });
  }, [navigate, priceOffer, priceOfferId]);

  /**
   * Submits the provider's price offer payload.
   */
  const submit = async ({ finalPriceLabor, finalPriceMaterial, fileId, note }: PriceOfferProvideFormData) => {
    if (!priceOfferId || finalPriceLabor === '' || finalPriceMaterial === '') {
      /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
      return;
    }

    try {
      const action = getAction('PriceOfferSubmit');
      await action({
        parameters: { priceOfferId },
        payload: {
          finalPriceLabor: Number(finalPriceLabor),
          finalPriceMaterial: Number(finalPriceMaterial),
          fileId,
          note: note.trim() === '' ? undefined : note.trim(),
        },
      });

      showNotification({
        color: SUCCESS_NOTIFICATION_COLOR,
        title: 'Cenová ponuka bola odovzdaná',
        message: 'Zadávateľ bol informovaný e-mailom.',
      });

      navigate(PRICE_OFFER_PAGE_PATH.insert({ priceOfferId }));
    } catch (error) {
      panic(error as Error);
    }
  };

  return (
    <DashboardLayout
      breadcrumbs={[
        { title: 'Cenové ponuky', link: PRICE_OFFERS_PAGE_PATH.original },
        { title: 'Vypracovanie cenovej ponuky' },
      ]}
      title="Vypracovanie cenovej ponuky"
    >
      {!priceOffer ? (
        <Stack align="center" pt={80}>
          <Loader />
        </Stack>
      ) : (
        <Stack spacing={24}>
          <PriceOfferProvideForm
            initialValues={{
              finalPriceLabor: priceOffer.finalPriceLabor ?? '',
              finalPriceMaterial: priceOffer.finalPriceMaterial ?? '',
              fileId: priceOffer.fileId ?? '',
              note: '',
            }}
            onSubmit={submit}
          />
        </Stack>
      )}
    </DashboardLayout>
  );
}
